import { z } from 'zod'

// Schémas pour les objets imbriqués
const creatorSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatar: z.string().nullable(),
})

const categorySchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
})

const tagSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
})

const countSchema = z.object({
  enrollments: z.number(),
  reviews: z.number(),
})

// Schema for block info (used in lastAccessedBlock, nextBlock, resumeBlock)
const blockInfoSchema = z.object({
  id: z.string(),
  title: z.string(),
  type: z.string().optional(),
  order: z.number().optional(),
  moduleId: z.string().optional(),
  moduleTitle: z.string().optional(),
  isCompleted: z.boolean().optional(),
  progress: z.any().optional(), // Progress data for the block
}).nullable().optional()

// Schema for quiz info (used in nextQuiz)
const quizInfoSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  moduleId: z.string().optional(),
  moduleTitle: z.string().optional(),
}).nullable().optional()

// Schema for module info (used in lastAccessedModule)
const moduleInfoSchema = z.object({
  id: z.string(),
  title: z.string(),
  order: z.number().optional(),
}).nullable().optional()

// Schema for user progress (optional, only if user is enrolled)
const userProgressSchema = z.object({
  id: z.string(),
  progress: z.number(),
  completedLessons: z.number(),
  totalLessons: z.number(),
  lastAccessedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
}).nullable().optional()

// Schema for enrollment (optional, only if user is enrolled)
const enrollmentSchema = z.object({
  id: z.string(),
  status: z.string(),
  enrolledAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  accessExpiresAt: z.string().nullable().optional(),
  accessBlockedAt: z.string().nullable().optional(),
  accessBlockedReason: z.string().nullable().optional(),
}).nullable().optional()

export const courseSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string().optional(),
  description: z.string().optional(),
  shortDescription: z.string().optional(),
  thumbnail: z.string().nullable().optional(),
  banner: z.string().nullable().optional(),
  price: z.string().optional(), // API returns price as string
  originalPrice: z.string().nullable().optional(),
  currency: z.string().optional(),
  duration: z.number().optional(), // Duration in seconds
  level: z.string().optional(),
  language: z.string().optional(),
  status: z.string().optional(),
  isPublished: z.boolean().optional(),
  isFeatured: z.boolean().optional(),
  isFree: z.boolean().optional(),
  maxStudents: z.number().optional(),
  currentStudents: z.number().optional(),
  rating: z.number().optional(),
  totalRatings: z.number().optional(),
  totalLessons: z.number().optional(),
  totalDuration: z.number().optional(),
  requirements: z.array(z.string()).optional(),
  objectives: z.array(z.string()).optional(),
  targetAudience: z.array(z.string()).optional(),
  certificate: z.boolean().optional(),
  certificateTemplate: z.string().nullable().optional(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  keywords: z.array(z.string()).optional(),
  creatorId: z.string().optional(),
  categoryId: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  deletedAt: z.string().nullable().optional(),
  publishedAt: z.string().nullable().optional(),
  creator: creatorSchema.optional(),
  category: categorySchema.optional(),
  tags: z.array(tagSchema).optional(),
  _count: countSchema.optional(),
  // Progress fields (optional, only if user is enrolled)
  userProgress: userProgressSchema,
  enrollment: enrollmentSchema,
  isStarted: z.boolean().optional(),
  lastAccessedBlock: blockInfoSchema,
  nextBlock: blockInfoSchema,
  resumeBlock: blockInfoSchema,
  nextQuiz: quizInfoSchema,
  lastAccessedModule: moduleInfoSchema,
  modules: z.array(z.any()).optional(), // Modules with progress (using moduleSchema from modules-schema.ts)
})

export type Course = z.infer<typeof courseSchema>

// Schema for the complete API response (more flexible)
export const coursesApiResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    courses: z.array(courseSchema),
    total: z.number(),
    page: z.number(),
    totalPages: z.number(),
  }).optional(), // Make data optional in case of errors
  messages: z.array(z.string()).optional(),
  errors: z.array(z.string()).optional(),
})

// Schema for array of courses (for backward compatibility)
export const coursesArraySchema = z.array(courseSchema)

// Simplified schema for debugging - accepts any object with at least an id and title
export const simpleCourseSchema = z.object({
  id: z.string(),
  title: z.string(),
}).passthrough() // Allow additional properties

// Schema for the actual API response structure (direct, no data wrapper)
export const directCoursesApiResponseSchema = z
  .any()
  .transform((raw) => {
    const v: any = raw as any;
    const candidates = [
      { base: v, key: 'courses' },
      { base: v?.data, key: 'courses' },
      { base: v?.courses, key: 'items' },
      { base: v?.data?.courses, key: 'items' },
    ];

    let coursesArr: any[] | undefined;
    let total: number | undefined;
    let page: number | undefined;
    let totalPages: number | undefined;

    for (const c of candidates) {
      const container = c?.base;
      if (!container) continue;
      const maybeArray = container[c.key];
      if (Array.isArray(container)) {
        coursesArr = container;
      } else if (Array.isArray(maybeArray)) {
        coursesArr = maybeArray;
        total = container.total ?? total;
        page = container.page ?? page;
        totalPages = container.totalPages ?? totalPages;
      } else if (Array.isArray(container?.rows)) {
        // Support { courses: { rows, count } } ou similaire
        coursesArr = container.rows;
        total = container.total ?? container.count ?? total;
        page = container.page ?? page;
        totalPages = container.totalPages ?? totalPages;
      }
      if (coursesArr) break;
    }

    // Valeurs par défaut si non fournies
    if (!Array.isArray(coursesArr)) coursesArr = [];
    if (typeof total !== 'number') total = coursesArr.length;
    if (typeof page !== 'number') page = 1;
    if (typeof totalPages !== 'number') totalPages = 1;

    return { courses: coursesArr, total, page, totalPages };
  })
  .pipe(
    z.object({
      courses: z.array(courseSchema),
      total: z.number(),
      page: z.number(),
      totalPages: z.number(),
    })
  )

export const simpleCoursesApiResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    courses: z.array(simpleCourseSchema),
  }).passthrough(), // Allow additional properties
}).passthrough() // Allow additional properties

// {
//     "id": "uuid-course-123",
//     "title": "Apprendre React de A à Z",
//     "slug": "apprendre-react-de-a-a-z",
//     "description": "Un cours complet pour maîtriser React, des concepts de base aux techniques avancées.",
//     "shortDescription": "Maîtrisez React en 30 heures",
//     "thumbnail": "https://example.com/thumbnail.jpg",
//     "banner": "https://example.com/banner.jpg",
//     "price": 9900,
//     "originalPrice": 19900,
//     "currency": "EUR",
//     "duration": 1800,
//     "level": "BEGINNER",
//     "language": "Français",
//     "status": "PUBLISHED",
//     "isPublished": true,
//     "isFeatured": false,
//     "isFree": false,
//     "maxStudents": 100,
//     "currentStudents": 25,
//     "rating": 4.5,
//     "totalRatings": 150,
//     "totalLessons": 45,
//     "totalDuration": 1800,
//     "requirements": [
//       "Connaissances de base en JavaScript",
//       "HTML/CSS"
//     ],
//     "objectives": [
//       "Maîtriser les hooks React",
//       "Créer des composants réutilisables"
//     ],
//     "targetAudience": [
//       "Développeurs débutants",
//       "Étudiants en informatique"
//     ],
//     "certificate": true,
//     "certificateTemplate": "certificate-template-v1",
//     "metaTitle": "Cours React - Apprendre React de A à Z",
//     "metaDescription": "Cours complet React pour débutants et intermédiaires",
//     "keywords": [
//       "react",
//       "javascript",
//       "frontend",
//       "développement web"
//     ],
//     "creatorId": "uuid-user-123",
//     "categoryId": "uuid-category-123",
//     "createdAt": "2024-01-01T00:00:00.000Z",
//     "updatedAt": "2024-01-15T10:30:00.000Z",
//     "deletedAt": null,
//     "publishedAt": "2024-01-10T09:00:00.000Z"
//   }